import React, { useCallback } from 'react';

import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { ConfirmationDialog } from '@eika/confirmation-dialog';
import { TrashcanAnimation } from '@eika/trashcan-animation';

import { useDeletePaymentItem } from '../../../fetchHooks/usePaymentItems';
import type { PaymentItemId } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import ActionResultInfo from '../../shared/ActionResultInfo/ActionResultInfo';
import { DeletionStatus } from '../EFaktura/DeleteEFaktura';

interface Props {
    paymentItemId: PaymentItemId;
    setDeleteStatus: (status: DeletionStatus) => void;
    deleteState: DeletionStatus;
}

const DeletePaymentItem: React.FC<Props> = ({ paymentItemId, setDeleteStatus, deleteState }) => {
    const translated = useTranslation();

    const deletePaymentItem = useDeletePaymentItem();

    const handleDeletePaymentItem = useCallback(() => {
        deletePaymentItem.mutate(paymentItemId);
    }, [paymentItemId, deletePaymentItem]);

    const cancelDeleteDialog = useCallback(() => {
        setDeleteStatus(DeletionStatus.INITIAL);
    }, [setDeleteStatus]);

    if (deletePaymentItem.isSuccess) {
        return (
            <ActionResultInfo
                title={translated.paymentItem.isDeleted}
                retry={handleDeletePaymentItem}
                icon={<TrashcanAnimation className="transfer-success-checkmark" />}
            />
        );
    }

    return (
        <ConfirmationDialog
            title={translated.paymentItem.sureYouWantToDelete}
            id={`delete-payment-item-dialog-${paymentItemId.value}`}
            footer={
                <div className="delete-recurring-payment-button__button-row">
                    {!deletePaymentItem.isPending && (
                        <LinkButton onClick={cancelDeleteDialog}>{translated.cancel}</LinkButton>
                    )}
                    <ButtonWithSpinner
                        size="small"
                        buttonType="positive"
                        onClick={handleDeletePaymentItem}
                        showSpinner={deletePaymentItem.isPending}
                    >
                        {translated.confirmDelete}
                    </ButtonWithSpinner>
                </div>
            }
            show={deletePaymentItem.isPending || deleteState === DeletionStatus.CONFIRMING}
            onExitFocus={cancelDeleteDialog}
            headingLevel={3}
        />
    );
};

export default DeletePaymentItem;
